import { useMemo, useState } from "react";
import { CATEGORIES, DEMOS, searchDemos } from "./catalog";
import type { DemoEntry } from "./catalog";

function DemoCard({ demo }: { readonly demo: DemoEntry }) {
  const live = demo.status === "live";
  const card = (
    <>
      <div className="demo-card-thumb">{demo.thumbnail}</div>
      <div className="demo-card-body">
        <p className="demo-card-meta">
          <span className="demo-card-category">{demo.category}</span>
          <span className={`demo-card-status ${demo.status}`}>
            {live ? "Live" : "Coming soon"}
          </span>
        </p>
        <h3>{demo.title}</h3>
        <p className="demo-card-description">{demo.description}</p>
        {demo.standards && demo.standards.length > 0 ? (
          <div className="demo-card-standards" aria-label="Curriculum standards">
            {demo.standards.map((std) => (
              <span key={std} className="demo-standard-badge">
                {std}
              </span>
            ))}
          </div>
        ) : null}
        <p className="demo-card-foot">
          {demo.level} · {demo.duration}
        </p>
      </div>
    </>
  );

  if (!live) {
    return (
      <article
        aria-label={`${demo.title} (coming soon)`}
        className="demo-card is-upcoming"
      >
        {card}
      </article>
    );
  }

  return (
    <a className="demo-card" href={demo.route}>
      {card}
    </a>
  );
}

export function Home() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<string | null>(null);
  const results = useMemo(
    () => searchDemos(DEMOS, query, category),
    [query, category],
  );

  const countFor = (name: string) =>
    DEMOS.filter((demo) => demo.category === name).length;

  return (
    <main className="pupil-shell">
      <header className="pupil-header">
        <p className="pupil-wordmark">Pupil</p>
        <h1>Live, interactive STEM demos</h1>
        <p className="pupil-tagline">
          See it move, change it yourself, understand it for good.
        </p>
        <search className="pupil-search" role="search">
          <input
            aria-label="Search demos"
            autoComplete="off"
            placeholder="Search demos — try “moon”, “seasons”, “orbit”, “MS-ESS1-1”…"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </search>
      </header>

      <div className="pupil-layout">
        <nav aria-label="Demo categories" className="pupil-rail">
          <p className="pupil-rail-title">Categories</p>
          <ul>
            <li>
              <button
                aria-pressed={category === null}
                className="pupil-rail-item"
                type="button"
                onClick={() => setCategory(null)}
              >
                All demos <span>{DEMOS.length}</span>
              </button>
            </li>
            {CATEGORIES.map((name) => {
              const count = countFor(name);
              return (
                <li key={name}>
                  <button
                    aria-pressed={category === name}
                    className="pupil-rail-item"
                    disabled={count === 0}
                    type="button"
                    onClick={() =>
                      setCategory((current) =>
                        current === name ? null : name,
                      )
                    }
                  >
                    {name} <span>{count === 0 ? "soon" : count}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <section aria-label="Demo results" className="pupil-results">
          {results.length === 0 ? (
            <p className="pupil-empty">
              No demos match “{query}”. Try “moon” or “seasons”.
            </p>
          ) : (
            <ul className="demo-grid">
              {results.map((demo) => (
                <li key={demo.id}>
                  <DemoCard demo={demo} />
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section
        aria-labelledby="visual-learning-heading"
        className="pupil-section pupil-features-section"
      >
        <h2 id="visual-learning-heading">
          Why Visual Explanations Work for Astronomy &amp; Physics
        </h2>
        <p className="pupil-section-intro">
          Traditional diagrams flatten 3D orbital mechanics into static 2D
          prints, leading to common misconceptions. Pupil connects cause and
          effect in real-time interactive space.
        </p>

        <div className="pupil-feature-grid">
          <article className="pupil-feature-card">
            <div className="pupil-feature-icon" aria-hidden="true">
              🌐
            </div>
            <h3>Dual Reference Frames</h3>
            <p>
              Observe the full 3D orbital system from space while simultaneously
              seeing the exact view from an observer on Earth. Bridge the mental
              gap between cosmic geometry and sky observations.
            </p>
          </article>

          <article className="pupil-feature-card">
            <div className="pupil-feature-icon" aria-hidden="true">
              ⏱️
            </div>
            <h3>Hands-On Time Scrubbing</h3>
            <p>
              Scrub smoothly through lunar cycles and annual orbits. Adjust
              playback speeds from 0.25× to 8×, step through guided tours, and
              inspect the geometry at critical astronomical stations.
            </p>
          </article>

          <article className="pupil-feature-card">
            <div className="pupil-feature-icon" aria-hidden="true">
              🚀
            </div>
            <h3>Frictionless Classroom Setup</h3>
            <p>
              Zero sign-ups, no accounts, and no ads. Runs instantly on any
              device — including school Chromebooks — with automatic 2D Canvas
              fallbacks and complete offline PWA capability.
            </p>
          </article>
        </div>
      </section>

      <section
        aria-labelledby="faq-heading"
        className="pupil-section pupil-faq-section"
      >
        <h2 id="faq-heading">Frequently Asked Questions</h2>
        <p className="pupil-section-intro">
          Visual answers to key concepts in orbital mechanics and astrophysics.
        </p>

        <div className="pupil-faq-list">
          <details className="pupil-faq-item">
            <summary>Why does the Moon have phases?</summary>
            <div className="pupil-faq-answer">
              <p>
                The Moon reflects sunlight — half of its sphere is always bathed
                in light. As the Moon completes its 29.5-day synodic orbit
                around Earth, our viewing angle of that sunlit side changes
                continuously. This creates the cycle of 8 distinct phases, from
                New Moon to Full Moon. Lunar eclipses occur when Earth casts its
                shadow on the Moon, but ordinary phases are purely a matter of
                orbital perspective.
              </p>
            </div>
          </details>

          <details className="pupil-faq-item">
            <summary>What causes Earth&apos;s seasons?</summary>
            <div className="pupil-faq-answer">
              <p>
                Seasons are caused by Earth&apos;s 23.44° axial tilt relative to
                its orbital plane, not by distance from the Sun. Because the tilt
                remains fixed in direction as Earth orbits, each hemisphere
                alternates between leaning toward the Sun (summer: longer days,
                more concentrated direct sunlight) and leaning away (winter:
                shorter days, spread-out sunlight).
              </p>
            </div>
          </details>

          <details className="pupil-faq-item">
            <summary>
              How does the Sun&apos;s altitude change with latitude and season?
            </summary>
            <div className="pupil-faq-answer">
              <p>
                An observer&apos;s local horizon determines which part of the sky
                is visible. At higher latitudes, the Sun stays lower along the
                horizon, creating long twilight and extreme day-length swings
                between solstices. At the equator, day length remains near 12
                hours all year, and the Sun climbs high near the zenith at noon.
              </p>
            </div>
          </details>

          <details className="pupil-faq-item">
            <summary>
              How are Pupil simulations aligned with science standards?
            </summary>
            <div className="pupil-faq-answer">
              <p>
                Pupil exhibits directly address NGSS standards, including
                <strong> MS-ESS1-1</strong> (Earth-Sun-Moon system models for
                lunar phases, eclipses, and seasons),
                <strong> MS-ESS1-2</strong> (gravity and orbital motion), and
                <strong> 5-ESS1-2</strong> (daily and seasonal sky patterns).
                They are built specifically for classroom demonstrations and
                student-led inquiry.
              </p>
            </div>
          </details>
        </div>
      </section>

      <footer className="pupil-footer">
        <div className="pupil-footer-content">
          <div>
            <p className="pupil-wordmark">Pupil</p>
            <p className="pupil-footer-desc">
              Live, interactive 3D STEM simulations designed for deep conceptual
              understanding through direct visual manipulation.
            </p>
          </div>
          <div className="pupil-footer-links">
            <p className="pupil-footer-title">Live Exhibits</p>
            <ul>
              <li>
                <a href="#/demos/moon-phases">Phases of the Moon</a>
              </li>
              <li>
                <a href="#/demos/earth-seasons">Earth&apos;s Seasons &amp; Tilt</a>
              </li>
              <li>
                <a href="#/demos/local-horizon">Local Horizon &amp; Altitude</a>
              </li>
            </ul>
          </div>
          <div className="pupil-footer-links">
            <p className="pupil-footer-title">Standards &amp; Pedagogy</p>
            <ul>
              <li>NGSS MS-ESS1-1 Earth-Sun-Moon</li>
              <li>NGSS MS-ESS1-2 Orbital Mechanics</li>
              <li>NGSS 5-ESS1-2 Sky Observations</li>
              <li>Accessible 2D/3D &amp; Offline PWA</li>
            </ul>
          </div>
        </div>
        <div className="pupil-footer-bottom">
          <p>
            Built by Rihone Labs. Free and open for students and educators
            everywhere.
          </p>
        </div>
      </footer>
    </main>
  );
}
